import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Copy, CalendarRange, CalendarOff } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function ymd(y, m0, d) {
  return `${y}-${String(m0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
const daysIn = (y, m0) => new Date(y, m0 + 1, 0).getDate();
const firstWeekday = (y, m0) => new Date(y, m0, 1).getDay();
const MONTH_LABEL = (y, m0) => new Date(y, m0).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
const WEEKDAY_LABELS = [
  { v: 1, l: "Mon" }, { v: 2, l: "Tue" }, { v: 3, l: "Wed" },
  { v: 4, l: "Thu" }, { v: 5, l: "Fri" }, { v: 6, l: "Sat" }, { v: 0, l: "Sun" },
];

export default function CalendarPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0..11
  const [poojas, setPoojas] = useState([]);
  const [data, setData] = useState({ capacities: [], statuses: [] });
  const [activeDate, setActiveDate] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [showCopy, setShowCopy] = useState(false);

  const enabledPoojas = useMemo(() => poojas.filter((p) => p.booking_enabled !== false), [poojas]);

  const load = useCallback(() => {
    api.get(`/calendar/month?year=${year}&month=${month + 1}`).then(({ data }) => setData(data));
  }, [year, month]);

  useEffect(() => { api.get("/poojas").then(({ data }) => setPoojas(data)); }, []);
  useEffect(() => { load(); }, [load]);

  // Index data per date for quick lookup
  const byDate = useMemo(() => {
    const map = {};
    for (const c of data.capacities) {
      map[c.date] ??= { caps: {}, status: null };
      map[c.date].caps[c.pooja_slug] = c;
    }
    for (const s of data.statuses) {
      map[s.date] ??= { caps: {}, status: null };
      map[s.date].status = s;
    }
    return map;
  }, [data]);

  const dim = daysIn(year, month);
  const fw = firstWeekday(year, month);
  const grid = Array.from({ length: 42 }, (_, i) => {
    const day = i - fw + 1;
    return day >= 1 && day <= dim ? day : null;
  });

  const prev = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">Calendar Management</h1>
          <p className="text-sm opacity-65 mt-1">Click any date to manage all pooja capacities and day status in a single screen.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowBulk(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-brand text-sm" data-testid="open-bulk-btn">
            <CalendarRange size={14} /> Bulk Configure
          </button>
          <button onClick={() => setShowCopy(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-ghost-brand text-sm" data-testid="open-copy-btn">
            <Copy size={14} /> Copy Month
          </button>
        </div>
      </div>

      {/* Month navigator */}
      <div className="mt-5 flex items-center gap-3">
        <button onClick={prev} className="p-2 border border-[color:var(--border)] rounded-md bg-white" data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <div className="font-heading text-xl min-w-[200px] text-center">{MONTH_LABEL(year, month)}</div>
        <button onClick={next} className="p-2 border border-[color:var(--border)] rounded-md bg-white" data-testid="cal-next"><ChevronRight size={16} /></button>
        <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }} className="ml-2 px-3 py-1.5 text-xs border border-[color:var(--border)] rounded-md bg-white" data-testid="cal-today">Today</button>
      </div>

      {/* Grid */}
      <div className="mt-4 bg-white border border-[color:var(--border)] rounded-md p-4">
        <div className="grid grid-cols-7 text-xs uppercase tracking-wider opacity-60 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="p-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {grid.map((day, i) => {
            if (!day) return <div key={i} className="aspect-square" />;
            const ds = ymd(year, month, day);
            const entry = byDate[ds];
            const status = entry?.status?.status || "open";
            const caps = entry ? Object.values(entry.caps) : [];
            const totalCap = caps.reduce((a, c) => a + (c.capacity || 0), 0);
            const totalBooked = caps.reduce((a, c) => a + (c.booked || 0), 0);
            const configured = caps.length;
            const isClosed = status !== "open";
            const isToday = ds === ymd(today.getFullYear(), today.getMonth(), today.getDate());
            return (
              <button
                key={i}
                onClick={() => setActiveDate(ds)}
                data-testid={`cal-day-${ds}`}
                className={`aspect-square rounded-md border text-left p-2 text-xs flex flex-col transition-all ${
                  isClosed ? "bg-red-50 border-red-200 hover:border-red-400" :
                  configured > 0 ? "bg-[color:var(--bg-2)] border-[color:var(--border)] hover:border-[color:var(--brand)]" :
                  "bg-white border-[color:var(--border)] hover:border-[color:var(--brand)]/50"
                } ${isToday ? "ring-2 ring-[color:var(--brand)]/40" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{day}</span>
                  {isClosed && <span className="text-[9px] uppercase tracking-wider text-red-700">{status}</span>}
                </div>
                {!isClosed && configured > 0 && (
                  <div className="mt-auto">
                    <div className="text-[color:var(--brand)] font-medium leading-tight">{totalBooked}/{totalCap}</div>
                    <div className="opacity-60 text-[10px] leading-tight">{configured} pooja{configured !== 1 ? "s" : ""}</div>
                  </div>
                )}
                {!isClosed && configured === 0 && (
                  <div className="mt-auto opacity-40 text-[10px]">Not configured</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-[11px] opacity-70">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[color:var(--bg-2)] border border-[color:var(--border)]" />Configured</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border border-[color:var(--border)]" />Not configured</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-50 border border-red-200" />Closed / Blocked</span>
        </div>
      </div>

      {activeDate && (
        <DayModal
          date={activeDate}
          poojas={enabledPoojas}
          existing={byDate[activeDate]}
          onClose={() => setActiveDate(null)}
          onSaved={() => { load(); }}
        />
      )}
      {showBulk && (
        <BulkModal
          poojas={enabledPoojas}
          year={year}
          month={month}
          onClose={() => setShowBulk(false)}
          onSaved={() => { load(); }}
        />
      )}
      {showCopy && (
        <CopyMonthModal
          year={year}
          month={month}
          onClose={() => setShowCopy(false)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

// ---- Day-detail modal: ALL poojas + status in one screen ----
function DayModal({ date, poojas, existing, onClose, onSaved }) {
  const initialCaps = useMemo(() => {
    const m = {};
    for (const p of poojas) m[p.slug] = existing?.caps?.[p.slug]?.capacity ?? 0;
    return m;
  }, [poojas, existing]);
  const initialStatus = existing?.status?.status || "open";
  const initialNote = existing?.status?.note || "";

  const [caps, setCaps] = useState(initialCaps);
  const [status, setStatus] = useState(initialStatus);
  const [note, setNote] = useState(initialNote);
  const [saving, setSaving] = useState(false);

  const bookedFor = (slug) => existing?.caps?.[slug]?.booked || 0;

  const applyAll = (value) => {
    const next = { ...caps };
    for (const p of poojas) next[p.slug] = Number(value) || 0;
    setCaps(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/calendar/day-batch", {
        date,
        capacities: poojas.map((p) => ({ pooja_slug: p.slug, capacity: Number(caps[p.slug]) || 0 })),
        status,
        note,
      });
      toast.success(`Saved for ${date}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const friendly = new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <Modal onClose={onClose} testid="day-edit-modal" title={friendly}>
      <div className="space-y-5 text-sm">
        {/* Day status */}
        <div>
          <div className="text-xs uppercase tracking-[0.18em] opacity-65 mb-2">Day Status</div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "open", l: "Open" },
              { v: "closed", l: "Closed" },
              { v: "blocked", l: "Holiday / Blocked" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setStatus(opt.v)}
                data-testid={`status-${opt.v}`}
                className={`py-2 rounded-md border text-sm transition-colors ${status === opt.v ? "bg-[color:var(--brand)] text-[#faf8f5] border-[color:var(--brand)]" : "bg-white border-[color:var(--border)] hover:border-[color:var(--brand)]/40"}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {status !== "open" && (
            <Input
              className="mt-2"
              placeholder="Note (e.g., Festival, maintenance)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="status-note"
            />
          )}
        </div>

        {/* Capacities */}
        <div className={status !== "open" ? "opacity-50" : ""}>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs uppercase tracking-[0.18em] opacity-65">Pooja Capacities</div>
            <div className="flex items-center gap-2 text-xs">
              <span className="opacity-60">Apply to all:</span>
              <input
                type="number"
                min={0}
                onChange={(e) => applyAll(e.target.value)}
                className="w-16 px-2 py-1 rounded-md border border-[color:var(--border)]"
                data-testid="apply-all-input"
                placeholder="0"
              />
            </div>
          </div>
          <div className="rounded-md border border-[color:var(--border)] divide-y divide-[color:var(--border)]">
            {poojas.map((p) => (
              <div key={p.slug} className="flex items-center gap-3 p-3" data-testid={`cap-row-${p.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs opacity-60">Booked: {bookedFor(p.slug)}</div>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={caps[p.slug] ?? 0}
                  onChange={(e) => setCaps({ ...caps, [p.slug]: e.target.value })}
                  className="w-24"
                  data-testid={`cap-input-${p.slug}`}
                  disabled={status !== "open"}
                />
              </div>
            ))}
          </div>
          <div className="text-[11px] opacity-60 mt-2">Lowering capacity below current booked count keeps the booking valid but blocks further bookings.</div>
        </div>
      </div>

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)]" data-testid="day-cancel">Cancel</button>
        <button onClick={save} disabled={saving} className="px-5 py-2 rounded-full btn-brand text-sm disabled:opacity-60" data-testid="day-save">
          {saving ? "Saving..." : "Save Day"}
        </button>
      </ModalFooter>
    </Modal>
  );
}

// ---- Bulk Configure modal: date range + multi-pooja + weekday filter + optional status ----
function BulkModal({ poojas, year, month, onClose, onSaved }) {
  // Default to start/end of current month
  const startDef = ymd(year, month, 1);
  const endDef = ymd(year, month, daysIn(year, month));

  const [start, setStart] = useState(startDef);
  const [end, setEnd] = useState(endDef);
  const [weekdays, setWeekdays] = useState([]); // empty = all days
  const [caps, setCaps] = useState(() => Object.fromEntries(poojas.map((p) => [p.slug, 0])));
  const [status, setStatus] = useState("open");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("template"); // template | close

  const toggleWd = (wd) => setWeekdays((w) => w.includes(wd) ? w.filter((x) => x !== wd) : [...w, wd]);

  const apply = async () => {
    if (!start || !end) return toast.error("Pick start and end dates");
    setSaving(true);
    try {
      if (mode === "close") {
        // Status-only change across range with optional weekday filter
        await api.post("/calendar/bulk-batch", {
          start_date: start,
          end_date: end,
          capacities: [],
          weekdays: weekdays.length ? weekdays : null,
          status: status === "open" ? "open" : status,
          note,
        });
      } else {
        await api.post("/calendar/bulk-batch", {
          start_date: start,
          end_date: end,
          capacities: poojas.map((p) => ({ pooja_slug: p.slug, capacity: Number(caps[p.slug]) || 0 })),
          weekdays: weekdays.length ? weekdays : null,
          status: null, // do not override status
          note: "",
        });
      }
      toast.success("Bulk update applied");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Bulk update failed");
    } finally {
      setSaving(false);
    }
  };

  const quickCloseMondays = () => {
    setMode("close");
    setStatus("closed");
    setWeekdays([1]); // Monday
  };

  return (
    <Modal onClose={onClose} testid="bulk-modal" title="Bulk Configure">
      <div className="space-y-5 text-sm">
        {/* Mode tabs */}
        <div className="inline-flex rounded-md border border-[color:var(--border)] overflow-hidden">
          <button
            onClick={() => setMode("template")}
            data-testid="bulk-mode-template"
            className={`px-4 py-2 text-xs ${mode === "template" ? "bg-[color:var(--brand)] text-[#faf8f5]" : "bg-white"}`}
          >
            Apply Capacity Template
          </button>
          <button
            onClick={() => setMode("close")}
            data-testid="bulk-mode-close"
            className={`px-4 py-2 text-xs border-l border-[color:var(--border)] ${mode === "close" ? "bg-[color:var(--brand)] text-[#faf8f5]" : "bg-white"}`}
          >
            Close / Block Dates
          </button>
        </div>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs opacity-60 mb-1">Start Date</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full rounded-md border border-[color:var(--border)] px-2 py-2 text-sm bg-white" data-testid="bulk-start" />
          </div>
          <div>
            <div className="text-xs opacity-60 mb-1">End Date</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full rounded-md border border-[color:var(--border)] px-2 py-2 text-sm bg-white" data-testid="bulk-end" />
          </div>
        </div>

        {/* Weekday filter */}
        <div>
          <div className="text-xs opacity-60 mb-1.5">Apply only to (optional)</div>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((w) => (
              <button
                key={w.v}
                onClick={() => toggleWd(w.v)}
                data-testid={`bulk-wd-${w.l}`}
                className={`px-3 py-1.5 text-xs rounded-full border ${weekdays.includes(w.v) ? "bg-[color:var(--brand)] text-[#faf8f5] border-[color:var(--brand)]" : "bg-white border-[color:var(--border)]"}`}
              >
                {w.l}
              </button>
            ))}
            {weekdays.length > 0 && (
              <button onClick={() => setWeekdays([])} className="text-xs underline opacity-65" data-testid="bulk-wd-clear">Clear</button>
            )}
          </div>
          <div className="text-[11px] opacity-60 mt-1">Leave empty to apply to every day in the range.</div>
        </div>

        {mode === "template" ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.18em] opacity-65">Pooja Capacities</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="opacity-60">Set all:</span>
                <input
                  type="number" min={0}
                  onChange={(e) => setCaps(Object.fromEntries(poojas.map((p) => [p.slug, Number(e.target.value) || 0])))}
                  className="w-16 px-2 py-1 rounded-md border border-[color:var(--border)]"
                  data-testid="bulk-apply-all"
                />
              </div>
            </div>
            <div className="rounded-md border border-[color:var(--border)] divide-y divide-[color:var(--border)]">
              {poojas.map((p) => (
                <div key={p.slug} className="flex items-center gap-3 p-3" data-testid={`bulk-cap-row-${p.slug}`}>
                  <div className="flex-1 min-w-0 font-medium truncate">{p.name}</div>
                  <Input
                    type="number" min={0}
                    value={caps[p.slug] ?? 0}
                    onChange={(e) => setCaps({ ...caps, [p.slug]: e.target.value })}
                    className="w-24"
                    data-testid={`bulk-cap-${p.slug}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-[0.18em] opacity-65">Set status to</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-44" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="blocked">Holiday / Blocked</SelectItem>
                  <SelectItem value="open">Re-open</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Note (e.g., Festival, maintenance)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="bulk-status-note" />
            <button onClick={quickCloseMondays} className="text-xs inline-flex items-center gap-1 text-[color:var(--brand)] hover:underline" data-testid="bulk-quick-mondays">
              <CalendarOff size={12} /> Quick: Close all Mondays
            </button>
          </div>
        )}
      </div>

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)]" data-testid="bulk-cancel">Cancel</button>
        <button onClick={apply} disabled={saving} className="px-5 py-2 rounded-full btn-brand text-sm disabled:opacity-60" data-testid="bulk-apply-btn">
          {saving ? "Applying..." : "Apply"}
        </button>
      </ModalFooter>
    </Modal>
  );
}

// ---- Copy Month modal ----
function CopyMonthModal({ year, month, onClose, onSaved }) {
  const next = month === 11 ? { y: year + 1, m: 0 } : { y: year, m: month + 1 };
  const [fromY, setFromY] = useState(year);
  const [fromM, setFromM] = useState(month);
  const [toY, setToY] = useState(next.y);
  const [toM, setToM] = useState(next.m);
  const [overwrite, setOverwrite] = useState(true);
  const [includeStatus, setIncludeStatus] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/calendar/copy-month", {
        from_year: fromY,
        from_month: fromM + 1,
        to_year: toY,
        to_month: toM + 1,
        include_status: includeStatus,
        overwrite,
      });
      toast.success(`Copied ${data.copied_capacities} capacity entries${includeStatus ? ` + ${data.copied_statuses} statuses` : ""}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Copy failed");
    } finally {
      setSaving(false);
    }
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ v: i, l: new Date(2000, i).toLocaleDateString("en-IN", { month: "long" }) }));
  const yearOptions = [year - 1, year, year + 1, year + 2];

  return (
    <Modal onClose={onClose} testid="copy-modal" title="Copy Month Configuration">
      <div className="space-y-5 text-sm">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] opacity-65 mb-2">From</div>
            <div className="flex gap-2">
              <Select value={String(fromM)} onValueChange={(v) => setFromM(Number(v))}>
                <SelectTrigger data-testid="copy-from-month"><SelectValue /></SelectTrigger>
                <SelectContent>{monthOptions.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={String(fromY)} onValueChange={(v) => setFromY(Number(v))}>
                <SelectTrigger className="w-28" data-testid="copy-from-year"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] opacity-65 mb-2">To</div>
            <div className="flex gap-2">
              <Select value={String(toM)} onValueChange={(v) => setToM(Number(v))}>
                <SelectTrigger data-testid="copy-to-month"><SelectValue /></SelectTrigger>
                <SelectContent>{monthOptions.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={String(toY)} onValueChange={(v) => setToY(Number(v))}>
                <SelectTrigger className="w-28" data-testid="copy-to-year"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="copy-overwrite" />
          <span>Overwrite existing entries in destination month</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={includeStatus} onChange={(e) => setIncludeStatus(e.target.checked)} data-testid="copy-include-status" />
          <span>Also copy day statuses (open/closed/blocked)</span>
        </label>
        <div className="text-[11px] opacity-65">Days are mapped by day-of-month (1→1, 15→15...). If the destination month is shorter, extra source days are skipped. Bookings in the destination are not affected.</div>
      </div>

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)]" data-testid="copy-cancel">Cancel</button>
        <button onClick={apply} disabled={saving} className="px-5 py-2 rounded-full btn-brand text-sm disabled:opacity-60" data-testid="copy-apply">
          {saving ? "Copying..." : "Copy Now"}
        </button>
      </ModalFooter>
    </Modal>
  );
}

// ---- Modal shell ----
function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-2xl my-10 shadow-2xl" data-testid={testid}>
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--border)]">
          <h3 className="font-heading text-2xl">{title}</h3>
          <button onClick={onClose} className="p-1 opacity-60 hover:opacity-100" aria-label="Close" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
function ModalFooter({ children }) {
  return <div className="border-t border-[color:var(--border)] px-6 py-4 flex justify-end gap-3 bg-[color:var(--bg)]/40">{children}</div>;
}
